import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { StoreProvider } from '../context/StoreContext';
import { lazy, Suspense } from 'react';
import ProtectedRoute from '../components/ProtectedRoute';
import AdminRoute from '../components/AdminRoute';

// Lazy load pages
const Home           = lazy(() => import('../pages/Home'));
const About          = lazy(() => import('../pages/About'));
const Services       = lazy(() => import('../pages/Services'));
const ServiceDetail  = lazy(() => import('../pages/ServiceDetail'));
const PortfolioPage  = lazy(() => import('../pages/PortfolioPage'));
const ProjectDetail  = lazy(() => import('../pages/ProjectDetail'));
const ContactPage    = lazy(() => import('../pages/ContactPage'));
const Courses        = lazy(() => import('../pages/Courses'));
const CourseDetail   = lazy(() => import('../pages/CourseDetail'));
const Software       = lazy(() => import('../pages/Software'));
const Cart           = lazy(() => import('../pages/Cart'));
const Login          = lazy(() => import('../pages/Login'));
const Signup         = lazy(() => import('../pages/Signup'));
const ForgotPassword = lazy(() => import('../pages/ForgotPassword'));
const ResetPassword  = lazy(() => import('../pages/ResetPassword'));
const Account        = lazy(() => import('../pages/Account'));
const ChangePassword = lazy(() => import('../pages/ChangePassword'));
const PaymentRejected= lazy(() => import('../pages/PaymentRejected'));
const PaymentSuccess = lazy(() => import('../pages/PaymentSuccess'));
const Admin          = lazy(() => import('../pages/Admin'));
const Privacy        = lazy(() => import('../pages/Privacy'));
const Terms          = lazy(() => import('../pages/Terms'));
const Sitemap        = lazy(() => import('../pages/Sitemap'));
const NotFound       = lazy(() => import('../pages/NotFound'));
const RedeemAudit    = lazy(() => import('../pages/RedeemAudit'));

// Componentes siempre visibles
import Navbar        from '../components/Navbar';
import Footer        from '../components/Footer';
import WhatsAppBubble from '../components/WhatsAppBubble';
import CookieBanner  from '../components/CookieBanner';
import ScrollToTop   from '../components/ScrollToTop';
import ErrorBoundary from '../components/ErrorBoundary';

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-bgDark">
      <div className="relative flex items-center justify-center">
        {/* Outer glow ring */}
        <div className="absolute w-24 h-24 rounded-full border-2 border-neonPurple/20 animate-ping" />
        {/* Spinning ring 1 */}
        <div className="absolute w-20 h-20 rounded-full border-t-2 border-r-2 border-neonPurple shadow-[0_0_20px_#8a2be2,0_0_40px_#8a2be2] animate-spin" />
        {/* Spinning ring 2 (reverse) */}
        <div
          className="absolute w-14 h-14 rounded-full border-b-2 border-l-2 border-purple-400 shadow-[0_0_15px_#a855f7]"
          style={{ animation: "spin 1.2s linear infinite reverse" }}
        />
        {/* Inner dot */}
        <div className="w-4 h-4 rounded-full bg-neonPurple shadow-[0_0_15px_#8a2be2,0_0_30px_#8a2be2] animate-pulse" />
      </div>
    </div>
  );
}

function AppRouter() {
  return (
    <StoreProvider>
      <Router>
        <ScrollToTop />
        <ErrorBoundary>
          <div className="min-h-screen bg-bgDark text-white">
            <Navbar />
            <WhatsAppBubble />
            <main>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  <Route path="/"              element={<Home />} />
                  <Route path="/about"         element={<About />} />
                  <Route path="/services"      element={<Services />} />
                  <Route path="/services/:id"  element={<ServiceDetail />} />
                  <Route path="/portfolio"     element={<PortfolioPage />} />
                  <Route path="/portfolio/:id" element={<ProjectDetail />} />
                  <Route path="/contact"       element={<ContactPage />} />
                  <Route path="/courses"       element={<Courses />} />
                  <Route path="/courses/:id"   element={<CourseDetail />} />
                  <Route path="/software"      element={<Software />} />
                  <Route path="/cart"          element={<Cart />} />
                  <Route path="/login"         element={<Login />} />
                  <Route path="/signup"        element={<Signup />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/reset-password"  element={<ResetPassword />} />
                  <Route path="/privacy"       element={<Privacy />} />
                  <Route path="/terms"         element={<Terms />} />
                  <Route path="/sitemap"       element={<Sitemap />} />
                  <Route path="/canjear-auditoria" element={<RedeemAudit />} />
                  <Route path="/pago-rechazado" element={
                    <ProtectedRoute><PaymentRejected /></ProtectedRoute>
                  } />
                  <Route path="/pago-exitoso" element={
                    <ProtectedRoute><PaymentSuccess /></ProtectedRoute>
                  } />
                  <Route path="/account" element={
                    <ProtectedRoute><Account /></ProtectedRoute>
                  } />
                  <Route path="/change-password" element={
                    <ProtectedRoute><ChangePassword /></ProtectedRoute>
                  } />
                  <Route path="/admin" element={
                    <AdminRoute><Admin /></AdminRoute>
                  } />
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </main>
            <Footer />
            <CookieBanner />
          </div>
        </ErrorBoundary>
      </Router>
    </StoreProvider>
  );
}

export default AppRouter;